import { useTranslation } from "react-i18next";

/** Severity level for a security finding */
export type Severity = "critical" | "high" | "medium";

/**
 * Identifies the source ruleset of a rule
 */
export type RuleSource = {
    /** The ruleset UUID */
    rulesetId: string;
    /** The ruleset name */
    rulesetName: string;
};

/**
 * A security finding from analyzing access rules and ruleset rules
 */
export type SecurityFinding = {
    /** The severity level */
    severity: Severity;
    /** The (already translated) finding title */
    title: string;
    /** The (already translated) finding description */
    description: string;
    /** Number of rules that match this finding */
    count: number;
    /** Rulesets that contain matching rules (deduplicated) */
    affectedRulesets: Array<RuleSource>;
};

/**
 * Minimal interface matching both internal and customer FullAccessRule types,
 * optionally tagged with the source ruleset
 */
export interface AnalyzableRule {
    /** The low port (or single port) */
    port: number;
    /** The high port if this is a range */
    port_range_end?: number | null;
    /** The protocol (e.g. "Tcp", "Udp") */
    protocol: string;
    /** Which ruleset this rule belongs to, if any */
    source?: RuleSource;
}

/**
 * Check if a rule uses a port-based protocol
 *
 * @param rule - the access rule
 *
 * @returns true if the protocol supports ports
 */
function isPortBasedProtocol(rule: AnalyzableRule): boolean {
    return rule.protocol === "Tcp" || rule.protocol === "Udp" || rule.protocol === "Sctp";
}

/**
 * Check if a rule allows all ports (wildcard)
 *
 * @param rule - the access rule
 *
 * @returns true if the rule covers all ports (0/1-65535)
 */
function isWildcardPort(rule: AnalyzableRule): boolean {
    return rule.port <= 1 && (rule.port_range_end ?? rule.port) >= 65535;
}

/**
 * Collect unique affected rulesets from a set of matching rules
 *
 * @param rules - matched rules
 *
 * @returns deduplicated list of affected rulesets
 */
function collectRulesets(rules: Array<AnalyzableRule>): Array<RuleSource> {
    const seen = new Set<string>();
    const result: Array<RuleSource> = [];
    for (const rule of rules) {
        if (rule.source && !seen.has(rule.source.rulesetId)) {
            seen.add(rule.source.rulesetId);
            result.push(rule.source);
        }
    }
    return result;
}

/**
 * Analyze access rules and ruleset rules for security findings
 *
 * @param rules - the list of rules to analyze (access rules + ruleset rules)
 *
 * @returns a list of security findings
 */
export function useSecurityFindings(rules: Array<AnalyzableRule>): Array<SecurityFinding> {
    const [t] = useTranslation("admin-dashboard");

    const portRules = rules.filter(isPortBasedProtocol);
    const findings: Array<SecurityFinding> = [];

    // Wildcard port rules — all TCP/UDP/SCTP ports open
    const wildcardRules = portRules.filter(isWildcardPort);
    if (wildcardRules.length > 0) {
        findings.push({
            severity: "critical",
            title: t("title.security-wildcard"),
            description: t("description.security-wildcard"),
            count: wildcardRules.length,
            affectedRulesets: collectRulesets(wildcardRules),
        });
    }

    return findings;
}
